import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { LogOut, Search, Shield, User as UserIcon } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, isAdmin, useSession } from "../lib/api";
import { roleLabel } from "../lib/format";
import { Logo } from "./ui";

export function AppShell() {
  const session = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const forced = session?.user.must_change_password;

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-line bg-bg/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4">
          <Link to="/" aria-label="Início">
            <Logo />
          </Link>
          {!forced && (
            <nav className="hidden items-center gap-1 text-sm sm:flex">
              <NavItem to="/" active={pathname === "/"}>
                Início
              </NavItem>
              {isAdmin(session) && (
                <NavItem to="/console" active={pathname.startsWith("/console")}>
                  Console
                </NavItem>
              )}
            </nav>
          )}
          <div className="flex-1" />
          {!forced && <SearchBox />}
          {session && <UserMenu />}
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ to, active, children }: { to: "/" | "/console"; active: boolean; children: string }) {
  return (
    <Link
      to={to}
      className={`rounded-md px-3 py-1.5 font-medium ${active ? "bg-raised text-fg" : "text-muted hover:text-fg"}`}
    >
      {children}
    </Link>
  );
}

function SearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length >= 2) void navigate({ to: "/busca", search: { q: q.trim() } });
  };
  return (
    <form onSubmit={submit} role="search" className="relative hidden md:block">
      <Search className="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-muted" aria-hidden />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar aulas"
        aria-label="Buscar aulas"
        className="field w-64 pl-9"
      />
    </form>
  );
}

function UserMenu() {
  const session = useSession()!;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const name = session.user.display_name || session.user.email;
  const initials = name
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu da conta"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-raised font-display text-sm font-bold text-accent ring-1 ring-line hover:ring-accent"
      >
        {initials}
      </button>
      {open && (
        <div role="menu" className="card absolute right-0 mt-2 w-60 overflow-hidden p-1 shadow-2xl">
          <div className="border-b border-line px-3 py-2">
            <p className="truncate text-sm font-semibold">{name}</p>
            <p className="truncate text-xs text-muted">
              {session.user.email} · {roleLabel[session.user.role]}
            </p>
          </div>
          <MenuItem onClick={() => (setOpen(false), void navigate({ to: "/busca" }))} icon={<Search />}>
            Buscar
          </MenuItem>
          <MenuItem onClick={() => (setOpen(false), void navigate({ to: "/conta" }))} icon={<UserIcon />}>
            Minha conta
          </MenuItem>
          {isAdmin(session) && (
            <MenuItem onClick={() => (setOpen(false), void navigate({ to: "/console" }))} icon={<Shield />}>
              Console
            </MenuItem>
          )}
          <MenuItem onClick={() => void api.logout()} icon={<LogOut />}>
            Sair
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, icon, children }: { onClick: () => void; icon: React.ReactElement; children: string }) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-raised [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-muted"
    >
      {icon}
      {children}
    </button>
  );
}
